// React Hooks
import React, { useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
// Custome Component
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import AccountStatusDialog from "../../components/account-status/AccountStatusDialog";
import EmployeeDetail from "./employee-detail/EmployeeDetail";
import EmployeeConfig from "./employee-config/EmployeeConfig";
// MUI Component
import { Stack, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Box from "@mui/material/Box";
// API
import { queryEmployeeStatusOptions, queryEmployeeDetail, updateEmployee, queryEmployeeConfig } from "./model";
// Others
import s from "./OfflineEmployeeDetail.module.scss";
import { useSnackbar } from "notistack";
import moment from "moment";
import { isNullish } from "utils/utils";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import usePageState from "utils/usePageState";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";
import Notice from "components/Notice/Notice";

// Locale keys
const EMPLOYEE_LOCALE_KEY = "page.employees.employeeDetail.";
const DIALOG_LOCALE_KEY = "page.dialogs.";

const TAB_LIST_STYLE = {
  "& .MuiTabs-indicator": {
    backgroundColor: "#309ea7",
  },
  "& .MuiTab-root": {
    whiteSpace: "nowrap",
  },
};
const TAB_STYLE = {
  "&.Mui-selected": {
    color: "#309ea7",
  },
  textTransform: "none",
};
// const TAB_PANEL_STYLE = { padding: 0 };

const PARTITION_KEYS = /** @type {const} */ (["detail", "config", "statusDialog"]);
/** @typedef {typeof PARTITION_KEYS[number]} offlineEmployeeDetailPartitionKeyType */

const OfflineEmployeeDetail = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { pageState } = usePageState({ pagePath: path });
  const { employeeId: stateEmployeeId } = pageState;
  const { enqueueSnackbar } = useSnackbar();
  const { callApis, PageWrapper, PageOuterContainer, PageStickyHeader, PageBody } = usePageWrapper({
    partitionKeys: PARTITION_KEYS,
  });

  const [employeeId, setEmployeeId] = useState("");
  const [employeeDetail, setEmployeeDetail] = useState({});
  const [employeeConfig, setEmployeeConfig] = useState({});
  const [tabValue, setTabValue] = useState("detail");
  const [isDetailLoading, setIsDetailLoading] = useState(true);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusRemarks, setStatusRemarks] = useState();
  const [isAccountStatusDialogOpen, setIsAccountStatusDialogOpen] =
    useState(false);
  const AGENT_STATUS_INIT_STATE = {
    status: "",
    lockAccountType: "",
    remark: "",
  };
  const [accountStatusDialogData, setAccountStatusDialogData] = useState(
    AGENT_STATUS_INIT_STATE,
  );

  const isStatusRemarksAvailable = useMemo(() => statusRemarks?.["TERMINATED"] && statusRemarks?.["SUSPENDED"], [statusRemarks]);

  useLayoutEffect(() => {
    if (stateEmployeeId) setEmployeeId(stateEmployeeId);
    else redirectFn("close", { tabId: path });
  }, [stateEmployeeId]);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  useLayoutEffect(() => {
    if (employeeId) {
      initialFetchDetails();
    }
  }, [employeeId]);

  const initialFetchDetails = async () => {
    try {
      setIsDetailLoading(true);
      setIsConfigLoading(true);
      const [statusOptionsResp, employeeDetailResp, employeeConfigResp] = await callApis([
        {
          api: queryEmployeeStatusOptions,
          partitionKeys: ["statusDialog"],
        },
        {
          api: queryEmployeeDetail,
          payload: employeeId,
          partitionKeys: ["detail", "statusDialog"],
        },
        {
          api: queryEmployeeConfig,
          payload: { employeeId },
          partitionKeys: ["config"],
        }
      ])

      if (!statusOptionsResp.isSuccess) {
        statusOptionsResp.isError && snackbar(
          t(EMPLOYEE_LOCALE_KEY + "toast.queryStatusOptions.failed"),
          "error",
        );
      } else {
        setStatusRemarks(statusOptionsResp?.data);
      }

      if (!employeeDetailResp.isSuccess) {
        employeeDetailResp.isError && snackbar(
          t(EMPLOYEE_LOCALE_KEY + "toast.queryDetail.failed"),
          "error",
        );
      } else {
        setEmployeeDetail(employeeDetailResp?.data);
      }

      if (!employeeConfigResp.isSuccess) {
        employeeConfigResp.isError && snackbar(
          t(EMPLOYEE_LOCALE_KEY + "toast.queryConfig.failed"),
          "error",
        );
      } else {
        setEmployeeConfig(employeeConfigResp?.data);
      }
    } catch (error) {
      console.error("Error fetching employee detail and config:", error);
    } finally {
      setIsDetailLoading(false);
      setIsConfigLoading(false);
    }
  };

  const fetchEmployeeDetail = async () => {
    try {
      setIsDetailLoading(true);
      const [employeeDetailResp] = await callApis([{
        api: queryEmployeeDetail,
        payload: employeeId,
        partitionKeys: ["detail"],
      }]);
      if (!employeeDetailResp.isSuccess) {
        employeeDetailResp.isError && snackbar(
          t(EMPLOYEE_LOCALE_KEY + "toast.queryDetail.failed"),
          "error",
        )
      } else {
        setEmployeeDetail(employeeDetailResp?.data);
      }
    } catch (error) {
      console.error("Error fetching employee detail:", error);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const fetchEmployeeConfig = async () => {
    try {
      setIsConfigLoading(true);
      const [employeeConfigResp] = await callApis([{
        api: queryEmployeeConfig,
        payload: { employeeId },
        partitionKeys: ["config"],
      }])
      if (!employeeConfigResp.isSuccess) {
        employeeConfigResp.isError && snackbar(
          t(EMPLOYEE_LOCALE_KEY + "toast.queryConfig.failed"),
          "error",
        )
      } else {
        setEmployeeConfig(employeeConfigResp?.data);
      }
    } catch (error) {
      console.error("Error fetching employee detail:", error);
    } finally {
      setIsConfigLoading(false);
    }
  };

  const warning = (employee) => {
    const { status, remark, terminatedDate } = employee;

    const alertList = [];

    // Account status warning
    if (["TERMINATED", "SUSPENDED"].includes(status)) {
      alertList.push(
        <Notice
          severity={status === "TERMINATED" ? "error" : "warning"}
        >
          {`Account is ${status.toLowerCase()}. Reason: ${remark} (${moment(terminatedDate).format("YYYY-MM-DD HH:mm:ss")})`}
        </Notice>
      );
    }

    if (alertList.length) return <>{alertList}</>;
    else return null;
  };

  // -------- Handle Status dialog -------- //
  const accountStatusDialog = {
    open: (data) => {
      setAccountStatusDialogData(data);
      setIsAccountStatusDialogOpen(true);
    },

    close: () => {
      accountStatusDialog.restore();
      setIsAccountStatusDialogOpen(false);
    },

    updatePayload: (fieldInput) => {
      setAccountStatusDialogData(fieldInput);
    },

    content: () => (
      <AccountStatusDialog
        data={accountStatusDialogData}
        remarksList={statusRemarks}
        updatePayload={accountStatusDialog.updatePayload}
      />
    ),

    restore: () => {
      setAccountStatusDialogData(AGENT_STATUS_INIT_STATE);
    },

    validationCheck: () => {
      if (accountStatusDialogData.status !== "ACTIVE") {
        const validationResult =
          !!accountStatusDialogData.reason &&
          !!accountStatusDialogData.reason !== "";
        if (!validationResult) {
          snackbar(
            t(DIALOG_LOCALE_KEY + "changeStatus.msgError") +
            " Reason must not be empty!",
            "error",
          );
        }
        return validationResult;
      }
      return true;
    },

    updateResult: async () => {
      if (accountStatusDialog.validationCheck()) {
        setSubmitting(true);
        const reasonList = statusRemarks[accountStatusDialogData.status];
        const remarkOthersKey = "OTHERS";

        let remarksend =
          reasonList && (reasonList[accountStatusDialogData.reason] ?? "");
        if (accountStatusDialogData.reason == remarkOthersKey)
          remarksend = accountStatusDialogData.remark;

        let payload = {
          email: employeeDetail.email,
          isStatusChange: accountStatusDialogData.status != employeeDetail.status,
          status: accountStatusDialogData.status,
        };

        if (!isNullish(accountStatusDialogData.reason))
          payload = {
            ...payload,
            lockAccountType: accountStatusDialogData.reason,
          };
        if (!isNullish(remarksend))
          payload = { ...payload, remark: remarksend };

        const [updateEmployeeResp] = await callApis([{
          api: updateEmployee,
          payload: payload,
          blockOnError: false,
          blockOnUnauthorized: false,
        }])
        if (!updateEmployeeResp.isSuccess) {
          updateEmployeeResp.isError && snackbar(
            t(DIALOG_LOCALE_KEY + "changeStatus.msgFailed") +
            responseCodeMapping[responseBody.responseCode ?? "error"]["en"],
            "error",
          );
        } else {
          snackbar(
            t(
              DIALOG_LOCALE_KEY +
              "changeStatus.msgSuccess." +
              accountStatusDialogData.status.toLowerCase(),
            ),
            "success",
          );
        }
        setSubmitting(false);
        accountStatusDialog.close();
        await fetchEmployeeDetail();
        await fetchEmployeeConfig();
      }
    },
  };
  // -------- Handle Status dialog -------- //

  const header = () => (
    <>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t(`${EMPLOYEE_LOCALE_KEY}header.employeeName`)}:{" "}
        {employeeDetail?.email}
      </Typography>
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Typography>
          {t(`${EMPLOYEE_LOCALE_KEY}header.employeeId`)}:{" "}
          {employeeDetail?.employeeId}
        </Typography>
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          {t(`${EMPLOYEE_LOCALE_KEY}header.status`)}
          <Stack
            className={[
              s.statusContainer,
              employeeDetail?.status === "SUSPENDED"
                ? s.statusSuspended
                : employeeDetail?.status === "TERMINATED"
                  ? s.statusTerminated
                  : s.statusActive,
            ]}
          >
            {employeeDetail?.status ?? "ACTIVE"}
          </Stack>
          <ButtonMAS
            variant="text-only"
            sx={{ display: employeeDetail?.status === "TERMINATED" ? "none" : "" }}
            onClick={() => {
              accountStatusDialog.open(employeeDetail);
            }}
            disabled={isDetailLoading || !isStatusRemarksAvailable}
          >
            <EditIcon sx={{ color: "#309ea7" }} />
          </ButtonMAS>
        </Stack>
      </Stack>
      <>{warning(employeeDetail)}</>

      <TabList
        onChange={(event, newValue) => setTabValue(newValue)}
        sx={TAB_LIST_STYLE}
        variant="standard" // switch to scrollable if too many tabs
        scrollButtons="auto"
      >
        <Tab
          label={t(`${EMPLOYEE_LOCALE_KEY}detail.title`)}
          value="detail"
          sx={TAB_STYLE}
        />
        <Tab
          label={t(`${EMPLOYEE_LOCALE_KEY}config.title`)}
          value="config"
          sx={TAB_STYLE}
        />
      </TabList>
    </>
  );

  const tabs = () => {
    return (
      <>
        {/* Use display attribute to prevent re-fetching the data */}
        <Box display={tabValue === "detail" ? "unset" : "none"}>
          <EmployeeDetail
            callApis={callApis}
            employeeDetail={employeeDetail}
            redirectFn={redirectFn}
            fetchEmployeeDetail={fetchEmployeeDetail}
          />
        </Box>
        <Box display={tabValue === "config" ? "unset" : "none"}>
          <EmployeeConfig
            callApis={callApis}
            isLoadingState={[isConfigLoading, setIsConfigLoading]}
            employeeConfig={employeeConfig}
            fetchEmployeeConfig={fetchEmployeeConfig}
          />
        </Box>
      </>
    );
  };

  return (
    <PageOuterContainer>
      <TabContext value={tabValue}>
        <PageStickyHeader noBottomPadding>
          {header()}
        </PageStickyHeader>
        <PageWrapper
          noPadding
          partitionKey={tabValue}
          isLoading={(() => {
            switch (tabValue) {
              case "detail":
                return isDetailLoading;
              case "config":
                return isConfigLoading;
            }
          })()}>
          <PageBody>
            {tabs()}
          </PageBody>
        </PageWrapper>
        {/* The employee status handler */}
        <SimpleDialog
          open={isAccountStatusDialogOpen}
          dialogOncloseFn={accountStatusDialog.close}
          title={t(DIALOG_LOCALE_KEY + "changeStatus.title")}
          content={accountStatusDialog.content()}
          positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
          negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
          positiveActionLoading={submitting}
          negativeActionDisabled={submitting}
          confirmFn={
            !submitting
              ? accountStatusDialog.updateResult
              : () => {
                console.log("submitting?", submitting);
              }
          }
        />
      </TabContext>
    </PageOuterContainer>
  );
};

export default React.memo(OfflineEmployeeDetail);
